import { defineStore } from 'pinia'
import { computed, shallowRef } from 'vue'

import { getSmartSocietyAuthClient } from '@/api/smartSocietyAuthClient'
import { getSmartSocietyAuthConfig } from '@/api/smartSocietyAuthConfig'
import { AuthenticationRequiredError } from '@/api/errors'
import type { SmartSocietyAuthAppState, SmartSocietyUser } from '@/types/auth'

function isMockAuthEnabled(): boolean {
  return import.meta.env.DEV && new URLSearchParams(window.location.search).get('mock-auth') === '1'
}

function getMockUser(): SmartSocietyUser {
  return {
    sub: 'mock-user',
    name: 'Mock User',
    given_name: 'Mock User',
    family_name: 'User',
    email: 'mock.user@example.com'
  }
}

function hasAuthorizationParams(search: string): boolean {
  const params = new URLSearchParams(search)

  return params.has('code') && params.has('state')
}

function getAuthorizationError(search: string): string | null {
  const params = new URLSearchParams(search)

  if (!params.has('error')) {
    return null
  }

  return params.get('error_description') ?? params.get('error') ?? 'Authentication failed.'
}

function isAuthenticationRequiredError(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('error' in error)) {
    return false
  }

  const authError = (error as { error?: string }).error

  return (
    authError === 'login_required' ||
    authError === 'consent_required' ||
    authError === 'interaction_required' ||
    authError === 'missing_refresh_token' ||
    authError === 'access_denied' ||
    authError === 'unauthorized' ||
    authError === 'unauthorized_client' ||
    authError === 'invalid_grant'
  )
}

function clearAuthorizationParams(): void {
  const url = new URL(window.location.href)

  url.searchParams.delete('code')
  url.searchParams.delete('state')
  url.searchParams.delete('error')
  url.searchParams.delete('error_description')

  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`)
}

export const useSmartSocietyAuthStore = defineStore('smartSocietyAuth', () => {
  const user = shallowRef<SmartSocietyUser | null>(null)
  const isInitializing = shallowRef(false)
  const isInitialized = shallowRef(false)
  const authError = shallowRef<string | null>(null)
  const pendingReturnTo = shallowRef<string | null>(null)
  const currentAccessToken = shallowRef<string | null>(null)
  let initializePromise: Promise<void> | null = null

  const isAuthenticated = computed(() => !!user.value)

  function resetSession() {
    user.value = null
    currentAccessToken.value = null
  }

  function setAuthErrorMessage(error: unknown) {
    authError.value = error instanceof Error ? error.message : 'Authentication failed.'
  }

  async function syncSession(forceRefresh = false): Promise<string | null> {
    const config = getSmartSocietyAuthConfig()
    const client = await getSmartSocietyAuthClient()

    try {
      const accessToken = await client.getTokenSilently({
        cacheMode: forceRefresh ? 'off' : 'on',
        timeoutInSeconds: 10,
        authorizationParams: {
          redirect_uri: config.redirectUri,
          audience: config.audience
        }
      })

      currentAccessToken.value = accessToken
      user.value = (await client.getUser()) as SmartSocietyUser | null
      authError.value = null

      return accessToken
    } catch (error) {
      resetSession()

      if (isAuthenticationRequiredError(error)) {
        setAuthErrorMessage(error)
        return null
      }

      setAuthErrorMessage(error)
      throw error
    }
  }

  async function initialize(): Promise<void> {
    if (isInitialized.value) {
      return
    }

    if (initializePromise) {
      return initializePromise
    }

    initializePromise = (async () => {
      isInitializing.value = true

      try {
        if (isMockAuthEnabled()) {
          user.value = getMockUser()
          currentAccessToken.value = 'mock-access-token'
          authError.value = null
          pendingReturnTo.value = null
          return
        }

        const client = await getSmartSocietyAuthClient()

        const processingCallback = hasAuthorizationParams(window.location.search)
        const authorizationError = getAuthorizationError(window.location.search)

        if (authorizationError) {
          resetSession()
          authError.value = authorizationError
          clearAuthorizationParams()
          return
        }

        if (processingCallback) {
          const callbackResult = await client.handleRedirectCallback<SmartSocietyAuthAppState>()
          pendingReturnTo.value = callbackResult.appState?.returnTo ?? '/'
          clearAuthorizationParams()
        }

        await syncSession()
      } catch (error) {
        resetSession()
        setAuthErrorMessage(error)

        if (hasAuthorizationParams(window.location.search)) {
          clearAuthorizationParams()
        }
      } finally {
        isInitialized.value = true
        isInitializing.value = false
        initializePromise = null
      }
    })()

    return initializePromise
  }

  async function login(returnTo = '/'): Promise<void> {
    if (isMockAuthEnabled()) {
      user.value = getMockUser()
      currentAccessToken.value = 'mock-access-token'
      authError.value = null
      pendingReturnTo.value = returnTo
      return
    }

    const config = getSmartSocietyAuthConfig()
    const client = await getSmartSocietyAuthClient()

    authError.value = null

    await client.loginWithRedirect<SmartSocietyAuthAppState>({
      appState: { returnTo },
      authorizationParams: {
        redirect_uri: config.redirectUri,
        audience: config.audience
      }
    })
  }

  async function logout(): Promise<void> {
    if (isMockAuthEnabled()) {
      resetSession()
      authError.value = null
      pendingReturnTo.value = null
      return
    }

    const config = getSmartSocietyAuthConfig()
    const client = await getSmartSocietyAuthClient()

    user.value = null
    currentAccessToken.value = null
    authError.value = null
    pendingReturnTo.value = null

    await client.logout({
      logoutParams: {
        returnTo: config.logoutReturnTo
      }
    })
  }

  async function getAccessToken(forceRefresh = false): Promise<string> {
    await initialize()

    const accessToken = await syncSession(forceRefresh)

    if (!accessToken) {
      throw new AuthenticationRequiredError()
    }

    return accessToken
  }

  async function reAuthenticate(): Promise<void> {
    await initialize()
  }

  return {
    user,
    authError,
    pendingReturnTo,
    currentAccessToken,
    isAuthenticated,
    isInitialized,
    isInitializing,
    initialize,
    login,
    logout,
    getAccessToken,
    reAuthenticate
  }
})
